import { Grid, Tabs, Typography } from '@mui/material';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import EstablishmentDataTabContent from '../modules/EstablishmentData';
import BackdropComponent from '../../../components/Backdrop/BackdropComponent';
import useCarcinogenicReportPrecharge from '../hooks/useCarcinogenicReportPrecharge';
import { useTranslation } from 'react-i18next';
import useTranslate from '../../../hooks/useTranslate';
import { CarcinogenicReportTab } from '../components/Tab';
import SubstancesAndPreventiveMeasuresTabContent from '../modules/SubstancesAndPreventiveMeasures';
import { useAppSelector } from '../../../hooks/reduxHooks';
import { useDispatch } from 'react-redux';
import {
    setCarcinogenicReporParametricData,
    setCarcinogenicReportCIIUCodes,
    setCarcinogenicReportCurrentTab
} from '../../../redux/slices/carcinogenicReportSlice';
import {
    useGetCarcinogenicParametricDataQuery,
    useGetCarcinogenicSrtValidacionesQuery
} from '../../../services/carcinogenicApi';
import { contarCorreccionesPorPaso } from '../pasoDeCorreccion';
import ProvidersTabContent from '../modules/Providers';
import RadiologicalEquipmentTabContent from '../modules/RadiologicalEquipment';
import WorkersAndMedicalExaminationsTabContent from '../modules/WorkersAndMedicalExaminations';
import { ResponsiblePartiesAndContractorsTabContent } from '../modules/ResponsiblePartiesAndContractors';
import { useGetCIIUCodesQuery } from '../../../services/companyApi';
import useCarcinogenicTabStates from '../hooks/useCarcinogenicTabStates';
import useAlerts from '../hooks/useAlerts';
import { TabPanel } from '../../../components/TabPanel';
import PanelValidacionSrt from '../components/PanelValidacionSrt';
import useIrAPaso from '../hooks/useIrAPaso';
import { TAB_INDEX } from '../pasos';

// Vive en `pasos.ts` (sin dependencias) y se re-exporta acá para no cambiar los
// imports existentes: el slice de redux lo importaba desde esta página, que
// arrastra todo el módulo.
export { TAB_INDEX };

export type HandleTabChange = {
    (event: React.SyntheticEvent, newValue: TAB_INDEX): void;
    (newValue: TAB_INDEX): void;
};

const CarcinogenicReport = ({ onlyVisual = false }: { onlyVisual?: boolean }) => {
    const dispatch = useDispatch();
    const { data: ciiuCodes } = useGetCIIUCodesQuery(null);
    const {
        IS_ESTABLISHMENT_DATA_COMPLETE,
        IS_SUBSTANCES_DATA_COMPLETE,
        IS_PROVIDERS_AND_BUYERS_DATA_COMPLETE,
        IS_RADIOLOGICAL_EQUIPMENT_DATA_COMPLETE,
        IS_WORKERS_AND_MEDICAL_EXAMS_DATA_COMPLETE,
        IS_RESPONSIBLES_AND_CONTRACTORS_DATA_COMPLETE,
        IS_REPORT_READY_TO_PRESENT,
        FALTANTES_PARA_ENVIAR
    } = useCarcinogenicTabStates();

    const selectedCarcinogenicReportId = useAppSelector(
        (state) => state?.carcinogenicReport?.selectedCarcinogenicReportId
    );
    const storeCurrentTab = useAppSelector((state) => state?.carcinogenicReport?.currentTab);
    const { data: carcinogenicParametricData, isFetching: isFetchingCarcinogenicParametricData } =
        useGetCarcinogenicParametricDataQuery({});

    const handleTabChange: HandleTabChange = (
        eventOrValue: React.SyntheticEvent | TAB_INDEX,
        maybeValue?: TAB_INDEX
    ) => {
        const newValue = typeof eventOrValue === 'number' ? eventOrValue : maybeValue!;
        dispatch(setCarcinogenicReportCurrentTab(newValue));
    };

    // 'Ir al paso' del panel: cambia de paso y baja hasta la barra de pasos.
    const { barraPasosRef, irAPaso } = useIrAPaso(handleTabChange);

    const { t } = useTranslation('carcinogenicReport');
    const { translate } = useTranslate(t);

    const { showCancelCarcinogenicReportAlert } = useAlerts({ translate: translate });
    const {
        carcinogeniReportInitialData,
        isFetchingCarcinogenicReportInitialData,
        carcinogenicReportDataSuccess
    } = useCarcinogenicReportPrecharge(selectedCarcinogenicReportId);

    // El contrato pudo darse de baja después de creado el informe: si
    // ocurrió, el informe pasa a ser de solo lectura aunque se haya entrado
    // por la ruta de edición.
    const effectiveOnlyVisual =
        onlyVisual || carcinogeniReportInitialData?.data?.contrato?.activo === false;

    useEffect(() => {
        dispatch(setCarcinogenicReporParametricData(carcinogenicParametricData?.data));
    }, [carcinogenicParametricData]);

    useEffect(() => {
        if (ciiuCodes?.data?.codigos) {
            dispatch(setCarcinogenicReportCIIUCodes(ciiuCodes));
        }
    }, [ciiuCodes]);

    // Misma query que el panel (RTK Query la comparte): el contador de cada paso
    // cuenta sólo los bloqueantes, que son los que impiden enviar.
    const { data: validaciones } = useGetCarcinogenicSrtValidacionesQuery(
        selectedCarcinogenicReportId,
        { skip: !selectedCarcinogenicReportId }
    );
    const correccionesPorPaso = useMemo(
        () =>
            effectiveOnlyVisual
                ? {}
                : contarCorreccionesPorPaso(
                      (validaciones?.data?.errores ?? []).filter(
                          (error: { severidad: string }) => error.severidad === 'error'
                      )
                  ),
        [validaciones, effectiveOnlyVisual]
    );

    // Mismas reglas que el `disabled` de cada pestaña.
    const pasoHabilitado = (paso: TAB_INDEX) =>
        paso === TAB_INDEX.ESTABLISHMENT_DATA ||
        (paso === TAB_INDEX.SUBSTANCES
            ? IS_ESTABLISHMENT_DATA_COMPLETE
            : IS_ESTABLISHMENT_DATA_COMPLETE && IS_SUBSTANCES_DATA_COMPLETE);

    useLayoutEffect(() => {
        dispatch(setCarcinogenicReportCurrentTab(TAB_INDEX.ESTABLISHMENT_DATA));
    }, []);

    return (
        <>
            {/*
             * Arriba de las tabs y no dentro de una: las reglas que evalúa son
             * de informe completo (cardinalidades, período de exposición, CIIU
             * del establecimiento), así que no pertenecen a ningún módulo en
             * particular y tienen que verse desde todos.
             */}
            <Grid mt={2}>
                <PanelValidacionSrt
                    idInforme={selectedCarcinogenicReportId}
                    onIrAPaso={irAPaso}
                    pasoHabilitado={pasoHabilitado}
                />
            </Grid>

            <Grid
                container
                direction="row"
                mt={2}
                ref={barraPasosRef}
                // Deja la barra debajo del AppBar fijo (64 px) al hacer scroll.
                sx={{ scrollMarginTop: '80px' }}
            >
                {/*
                 * Pasos como píldoras dentro de una barra redondeada. El estilo
                 * es local a esta pantalla (no toca el tema de MUI): el
                 * indicador de MUI se oculta porque el paso activo ya se marca
                 * con el relleno de su píldora (ver `Tab.tsx`).
                 */}
                <Tabs
                    value={storeCurrentTab}
                    onChange={handleTabChange}
                    sx={{
                        width: '100%',
                        minHeight: 0,
                        mb: 1.5,
                        p: 0.75,
                        bgcolor: '#FFFFFF',
                        border: '1px solid #E0EBED',
                        borderRadius: '12px',
                        '& .MuiTabs-indicator': { display: 'none' },
                        '& .MuiTabs-flexContainer': { flexWrap: 'wrap', gap: 0.5 }
                    }}
                >
                    <CarcinogenicReportTab
                        label={translate('tabNames.establishmentData')}
                        value={TAB_INDEX.ESTABLISHMENT_DATA}
                        correcciones={correccionesPorPaso[TAB_INDEX.ESTABLISHMENT_DATA]}
                        active={TAB_INDEX.ESTABLISHMENT_DATA === storeCurrentTab}
                        complete={IS_ESTABLISHMENT_DATA_COMPLETE}
                        onlyVisual={effectiveOnlyVisual}
                    />
                    <CarcinogenicReportTab
                        label={translate('tabNames.substancesAndPreventiveMeasures')}
                        value={TAB_INDEX.SUBSTANCES}
                        correcciones={correccionesPorPaso[TAB_INDEX.SUBSTANCES]}
                        active={TAB_INDEX.SUBSTANCES === storeCurrentTab}
                        complete={IS_SUBSTANCES_DATA_COMPLETE}
                        disabled={!IS_ESTABLISHMENT_DATA_COMPLETE}
                        onlyVisual={effectiveOnlyVisual}
                    />
                    <CarcinogenicReportTab
                        label={translate('tabNames.providersAndBuyers')}
                        value={TAB_INDEX.PROVIDERS_AND_BUYERS}
                        correcciones={correccionesPorPaso[TAB_INDEX.PROVIDERS_AND_BUYERS]}
                        active={TAB_INDEX.PROVIDERS_AND_BUYERS === storeCurrentTab}
                        complete={IS_PROVIDERS_AND_BUYERS_DATA_COMPLETE}
                        disabled={!IS_ESTABLISHMENT_DATA_COMPLETE || !IS_SUBSTANCES_DATA_COMPLETE}
                        onlyVisual={effectiveOnlyVisual}
                    />
                    <CarcinogenicReportTab
                        label={translate('tabNames.radiologicalEquipment')}
                        value={TAB_INDEX.RADIOLOGICAL_EQUIPMENT}
                        correcciones={correccionesPorPaso[TAB_INDEX.RADIOLOGICAL_EQUIPMENT]}
                        active={TAB_INDEX.RADIOLOGICAL_EQUIPMENT === storeCurrentTab}
                        complete={IS_RADIOLOGICAL_EQUIPMENT_DATA_COMPLETE}
                        disabled={!IS_ESTABLISHMENT_DATA_COMPLETE || !IS_SUBSTANCES_DATA_COMPLETE}
                        onlyVisual={effectiveOnlyVisual}
                    />
                    <CarcinogenicReportTab
                        label={translate('tabNames.workersAndMedicalExams')}
                        value={TAB_INDEX.WORKERS_AND_MEDICAL_EXAMS}
                        correcciones={correccionesPorPaso[TAB_INDEX.WORKERS_AND_MEDICAL_EXAMS]}
                        active={TAB_INDEX.WORKERS_AND_MEDICAL_EXAMS === storeCurrentTab}
                        complete={IS_WORKERS_AND_MEDICAL_EXAMS_DATA_COMPLETE}
                        disabled={!IS_ESTABLISHMENT_DATA_COMPLETE || !IS_SUBSTANCES_DATA_COMPLETE}
                        onlyVisual={effectiveOnlyVisual}
                    />
                    <CarcinogenicReportTab
                        label={translate('tabNames.responsiblesAndContractors')}
                        value={TAB_INDEX.RESPONSIBLES_AND_CONTRACTORS}
                        correcciones={correccionesPorPaso[TAB_INDEX.RESPONSIBLES_AND_CONTRACTORS]}
                        active={TAB_INDEX.RESPONSIBLES_AND_CONTRACTORS === storeCurrentTab}
                        complete={IS_RESPONSIBLES_AND_CONTRACTORS_DATA_COMPLETE}
                        disabled={!IS_ESTABLISHMENT_DATA_COMPLETE || !IS_SUBSTANCES_DATA_COMPLETE}
                        onlyVisual={effectiveOnlyVisual}
                    />
                </Tabs>
            </Grid>
            {!effectiveOnlyVisual &&
                (!IS_ESTABLISHMENT_DATA_COMPLETE || !IS_SUBSTANCES_DATA_COMPLETE) && (
                    <Typography
                        variant="body2"
                        data-testid="stepper-explicacion-bloqueo"
                        sx={{
                            color: '#5F6B70',
                            backgroundColor: '#FFFFFF',
                            paddingX: 2,
                            paddingTop: 1.5
                        }}
                    >
                        {!IS_ESTABLISHMENT_DATA_COMPLETE
                            ? translate('stepper.bloqueoSustancias')
                            : translate('stepper.bloqueoResto')}{' '}
                        {translate('stepper.guardado')}
                    </Typography>
                )}
            <Grid>
                <TabPanel value={storeCurrentTab} index={TAB_INDEX.ESTABLISHMENT_DATA}>
                    <EstablishmentDataTabContent
                        handleTabChange={handleTabChange}
                        showCancelCarcinogenicReportAlert={showCancelCarcinogenicReportAlert}
                        onlyVisual={effectiveOnlyVisual}
                    />
                </TabPanel>
                <TabPanel value={storeCurrentTab} index={TAB_INDEX.SUBSTANCES}>
                    <SubstancesAndPreventiveMeasuresTabContent
                        handleTabChange={handleTabChange}
                        showCancelCarcinogenicReportAlert={showCancelCarcinogenicReportAlert}
                        allowNext={IS_ESTABLISHMENT_DATA_COMPLETE && IS_SUBSTANCES_DATA_COMPLETE}
                        onlyVisual={effectiveOnlyVisual}
                    />
                </TabPanel>
                <TabPanel value={storeCurrentTab} index={TAB_INDEX.PROVIDERS_AND_BUYERS}>
                    <ProvidersTabContent
                        handleTabChange={handleTabChange}
                        showCancelCarcinogenicReportAlert={showCancelCarcinogenicReportAlert}
                        onlyVisual={effectiveOnlyVisual}
                    />
                </TabPanel>
                <TabPanel value={storeCurrentTab} index={TAB_INDEX.RADIOLOGICAL_EQUIPMENT}>
                    <RadiologicalEquipmentTabContent
                        handleTabChange={handleTabChange}
                        showCancelCarcinogenicReportAlert={showCancelCarcinogenicReportAlert}
                        onlyVisual={effectiveOnlyVisual}
                    />
                </TabPanel>
                <TabPanel value={storeCurrentTab} index={TAB_INDEX.WORKERS_AND_MEDICAL_EXAMS}>
                    <WorkersAndMedicalExaminationsTabContent
                        handleTabChange={handleTabChange}
                        showCancelCarcinogenicReportAlert={showCancelCarcinogenicReportAlert}
                        onlyVisual={effectiveOnlyVisual}
                    />
                </TabPanel>
                <TabPanel value={storeCurrentTab} index={TAB_INDEX.RESPONSIBLES_AND_CONTRACTORS}>
                    <ResponsiblePartiesAndContractorsTabContent
                        handleTabChange={handleTabChange}
                        showCancelCarcinogenicReportAlert={showCancelCarcinogenicReportAlert}
                        IS_REPORT_READY_TO_PRESENT={IS_REPORT_READY_TO_PRESENT}
                        FALTANTES_PARA_ENVIAR={FALTANTES_PARA_ENVIAR}
                        onlyVisual={effectiveOnlyVisual}
                    />
                </TabPanel>
            </Grid>
            <BackdropComponent
                fetching={
                    isFetchingCarcinogenicReportInitialData || isFetchingCarcinogenicParametricData
                }
            />
        </>
    );
};

export default CarcinogenicReport;
